import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import API from '../services/api';
import { bareDate } from '../utils/dates';
import COMPANY_INFO from '../data/companyInfo';
import DISCLAIMER from '../data/invoiceDisclaimer';
import './InvoicePrintView.css';
import printWhenReady from '../utils/printWhenReady';
import SignatureLines from './SignatureLines';
import ClientBlock from './ClientBlock';
import Loading from './Loading';
import { useFileName, documentName, DOC_TYPES } from '../utils/fileName';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
// Rental dates are bare dates (midnight UTC) — see utils/dates.js.
const dateStr = bareDate;

const PERIOD_LABEL = { daily: 'Diario', weekly: 'Semanal', monthly: 'Mensual' };

const RentalContractPrintView = () => {
  const { id } = useParams();
  const [rental, setRental] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get(`/rentals/${id}`).then(res => setRental(res.data)).catch(err => {
      console.error('Error loading rental:', err);
      setError('No se pudo cargar el alquiler.');
    });
  }, [id]);

  useEffect(() => {
    if (rental) {
      let cancelPrint = () => {};
      const t = setTimeout(() => { cancelPrint = printWhenReady(); }, 350);
      return () => { clearTimeout(t); cancelPrint(); };
    }
  }, [rental]);

  // Saved as PDF: <Cliente>ContratoDeAlquiler (utils/fileName.js).
  useFileName(rental && documentName(rental.client?.name, DOC_TYPES.contrato));

  if (error) return <div className="p-4 text-danger">{error}</div>;
  if (!rental) return <Loading page label="Cargando alquiler..." />;

  const client = rental.client || {};

  return (
    <div className="invoice-page-wrapper">
      <div className="no-print text-center mb-3">
        <button className="btn btn-primary" onClick={() => window.print()}>
          <i className="bi bi-printer" /> Imprimir / Guardar como PDF
        </button>
      </div>

      <div className="invoice-paper">
        {/* .invoice-body flows normally; .invoice-bottom (signatures + thank-you note) is pinned to the bottom of the page in print. */}
        <div className="invoice-body">
        <div className="invoice-header-row">
          <div className="invoice-company-block">
            <img src="/logo-print.png" alt="Intmed" className="invoice-logo" />
            <div style={{ fontWeight: 700, color: '#0B1B29' }}>{COMPANY_INFO.name}</div>
            <div>RNC: {COMPANY_INFO.rnc}</div>
            <div>{COMPANY_INFO.address}</div>
            <div>{COMPANY_INFO.phone} · {COMPANY_INFO.email}</div>
            <div>{COMPANY_INFO.website}</div>
          </div>
          <div className="invoice-meta-block">
            <div className="invoice-title">CONTRATO DE ALQUILER</div>
            <div>No.: {rental.number}</div>
            <div>Período de Facturación: {PERIOD_LABEL[rental.billingPeriod] || rental.billingPeriod}</div>
            <div>Inicio: {dateStr(rental.startDate)}</div>
            <div>Fin: {rental.endDate ? dateStr(rental.endDate) : 'Indefinido'}</div>
          </div>
        </div>

        <ClientBlock label="Arrendatario" client={client} address={client.address} />

        <table className="invoice-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Equipo</th>
              <th className="text-center">Cant.</th>
              <th className="text-end">Tarifa</th>
              <th className="text-center">ITBIS</th>
              <th className="text-end">Total/Ciclo</th>
            </tr>
          </thead>
          <tbody>
            {rental.items.map((item, i) => {
              const lineSubtotal = item.quantity * item.rate;
              const lineItbis = item.itbisExento ? 0 : lineSubtotal * (item.itbisRate ?? 0.18);
              return (
                <tr key={i}>
                  <td>{item.sku || '—'}</td>
                  <td>{item.description}</td>
                  <td className="text-center">{item.quantity}</td>
                  <td className="text-end">{money(item.rate)}</td>
                  <td className="text-center">{money(lineItbis)}</td>
                  <td className="text-end">{money(lineSubtotal + lineItbis)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="invoice-totals">
          <div className="invoice-totals-box">
            <div className="invoice-totals-row">
              <span>Subtotal</span>
              <span>{money(rental.subtotal)}</span>
            </div>
            <div className="invoice-totals-row">
              <span>ITBIS</span>
              <span>{money(rental.itbisTotal)}</span>
            </div>
            <div className="invoice-totals-row grand">
              <span>Total por Ciclo ({PERIOD_LABEL[rental.billingPeriod] || rental.billingPeriod})</span>
              <span>{money(rental.total)}</span>
            </div>
          </div>
        </div>

        {rental.notes && (
          <div className="invoice-bill-to">
            <div className="label">Notas</div>
            <div>{rental.notes}</div>
          </div>
        )}

        <div className="invoice-disclaimer">{DISCLAIMER}</div>

        </div>
        <div className="invoice-bottom">
        <SignatureLines clientLabel="Firma del cliente (arrendatario)" clientName={client.name} />

        <div className="invoice-footer-note">Gracias por su preferencia — {COMPANY_INFO.name}</div>
        </div>
      </div>
    </div>
  );
};

export default RentalContractPrintView;
